// Single shared Server-Sent Events connection to /api/mt5/stream.
// The server pushes MT5 quote/ohlc updates the moment an EA reports them, so
// hooks update instantly instead of polling. A slow fallback poll in the hooks
// covers the case where SSE is unavailable.

const BASE = (import.meta.env.VITE_API_BASE as string | undefined) || '';

export type Mt5StreamEvent =
  | { type: 'quotes'; broker: string; quotes: { sym: string; price: number; chg: number }[] }
  // `base` is the app's own name for the symbol (NVDA for a broker's NVDA.NAS).
  | { type: 'ohlc'; broker: string; symbol: string; base?: string; tf: string };

type Cb = (e: Mt5StreamEvent) => void;
const subs = new Set<Cb>();
let es: EventSource | null = null;
let retryT: ReturnType<typeof setTimeout> | null = null;
let retryMs = 2000;

function emit(e: Mt5StreamEvent) { for (const cb of subs) { try { cb(e); } catch { /* ignore */ } } }

function ensure() {
  if (es || retryT || typeof window === 'undefined' || typeof EventSource === 'undefined') return;
  const src = new EventSource(`${BASE}/api/mt5/stream`);
  es = src;
  src.addEventListener('open', () => { retryMs = 2000; });
  src.addEventListener('quotes', (ev: MessageEvent) => {
    try { emit({ type: 'quotes', ...JSON.parse(ev.data) }); } catch { /* ignore */ }
  });
  src.addEventListener('ohlc', (ev: MessageEvent) => {
    try { emit({ type: 'ohlc', ...JSON.parse(ev.data) }); } catch { /* ignore */ }
  });
  // EventSource retries a dropped connection by itself - but not a refused one.
  // When the API restarts, the retry lands on Cloudflare's 502, and any non-200
  // answer closes an EventSource for good (readyState CLOSED). Every open tab
  // then sat on the slow fallback poll until someone reloaded it, after each of
  // the server's restarts. A closed stream is rebuilt here, backing off to a
  // minute while the server is still away.
  src.addEventListener('error', () => {
    if (src.readyState !== 2 || es !== src) return;
    es = null;
    if (!subs.size) return;
    retryT = setTimeout(() => { retryT = null; if (subs.size) ensure(); }, retryMs);
    retryMs = Math.min(60000, retryMs * 2);
  });
}

/** Subscribe to MT5 push events. Opens the shared stream on first subscriber,
 *  closes it when the last one leaves. Returns an unsubscribe function. */
export function subscribeMt5(cb: Cb): () => void {
  subs.add(cb);
  ensure();
  return () => {
    subs.delete(cb);
    if (!subs.size) {
      if (es) { es.close(); es = null; }
      if (retryT) { clearTimeout(retryT); retryT = null; }
    }
  };
}
